import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { API_CONFIG } from '../config/api'
import { handleAccountSuspended } from '../utils/authErrorHandler'
import type {
    CmPartner,
    CmDeal,
    CmActivity,
    CmTsdCompany,
    CmTsdContact,
    CmPlaybook,
    CmContactGroup,
    CmTodaysActions,
    CmKpis,
    CmDiagnosticRow,
    CmSignal,
    CmProfile,
    CmSettings,
    CmPartnerTier,
    CmRelationshipStage,
    CmDealStage,
    CmLostReason,
    CmActivityType,
    CmMeetingType,
    CmMeetingNature,
    CmSentiment,
    CmStepStatus,
    CmPriority
} from '../types/cm'

const cm = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout
})

cm.interceptors.response.use(
    (r) => r,
    (error) => {
        Sentry.addBreadcrumb({
            category: 'cm-api',
            message: `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'Network'}`,
            level: error.response?.status >= 500 ? 'error' : 'warning'
        })
        if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_SUSPENDED') {
            handleAccountSuspended()
        }
        return Promise.reject(error)
    }
)

function authHeaders(token: string) {
    return { headers: { Authorization: `Bearer ${token}` } }
}

// ============================================
// Onboarding (public — no token)
// ============================================

export interface SupplierSearchResult {
    id: string
    name: string
    slug: string
    logo_url: string | null
}

export async function searchSuppliersPublic(q: string): Promise<SupplierSearchResult[]> {
    if (!q || q.length < 2) return []
    const res = await cm.get('/api/cm-onboarding/supplier-search', { params: { q } })
    return res.data.suppliers
}

export interface CmSignupRequest {
    email: string
    password: string
    name: string
    title?: string
    supplierId?: string
    supplierNameRaw?: string
    territoryRegion?: 'NE' | 'SE' | 'MW' | 'SW' | 'W'
}

export async function cmSignup(data: CmSignupRequest) {
    const res = await cm.post('/api/cm-onboarding/signup', data)
    return res.data
}

export async function cmCompleteOnboarding(token: string) {
    const res = await cm.post('/api/cm-onboarding/complete', {}, authHeaders(token))
    return res.data
}

// ============================================
// Profile + settings
// ============================================

export async function getCmProfile(token: string): Promise<{ channelManager: CmProfile }> {
    const res = await cm.get('/api/cm/me', authHeaders(token))
    return res.data
}

export async function updateCmSettings(token: string, patch: Partial<CmSettings>): Promise<{ settings: CmSettings }> {
    const res = await cm.patch('/api/cm/settings', patch, authHeaders(token))
    return res.data
}

// ============================================
// Partners
// ============================================

export async function listCmPartners(
    token: string,
    filters: { tier?: CmPartnerTier; stage?: CmRelationshipStage; pulse?: string } = {}
): Promise<{ partners: CmPartner[] }> {
    const res = await cm.get('/api/cm/partners', { ...authHeaders(token), params: filters })
    return res.data
}

export async function getCmPartner(token: string, id: string): Promise<{ partner: CmPartner }> {
    const res = await cm.get(`/api/cm/partners/${id}`, authHeaders(token))
    return res.data
}

export interface CreateCmPartnerRequest {
    name: string
    company: string
    title?: string
    email?: string
    phone?: string
    linkedin?: string
    locationCity?: string
    locationState?: string
    tier: CmPartnerTier
    relationshipStage?: CmRelationshipStage
    source?: string
    mrr?: number
}

export async function createCmPartner(token: string, data: CreateCmPartnerRequest): Promise<{ partner: CmPartner }> {
    const res = await cm.post('/api/cm/partners', data, authHeaders(token))
    return res.data
}

export async function updateCmPartner(token: string, id: string, patch: Partial<CmPartner>): Promise<{ partner: CmPartner }> {
    const res = await cm.patch(`/api/cm/partners/${id}`, patch, authHeaders(token))
    return res.data
}

export async function deleteCmPartner(token: string, id: string) {
    const res = await cm.delete(`/api/cm/partners/${id}`, authHeaders(token))
    return res.data
}

export interface BulkImportResult {
    partners: number
    deals: number
    activities: number
    partnerIdMap: Record<string, string>
    dealIdMap: Record<string, string>
}

export async function bulkImportCm(token: string, data: { partners?: Array<Record<string, unknown>>; deals?: Array<Record<string, unknown>>; activities?: Array<Record<string, unknown>> }): Promise<BulkImportResult> {
    const res = await cm.post('/api/cm/import', data, authHeaders(token))
    return res.data
}

// ============================================
// Deals
// ============================================

export async function listCmDeals(
    token: string,
    filters: { stage?: CmDealStage; health?: string; timeframe?: string } = {}
): Promise<{ deals: CmDeal[] }> {
    const res = await cm.get('/api/cm/deals', { ...authHeaders(token), params: filters })
    return res.data
}

export async function getCmDeal(token: string, id: string): Promise<{ deal: CmDeal }> {
    const res = await cm.get(`/api/cm/deals/${id}`, authHeaders(token))
    return res.data
}

export interface CreateCmDealRequest {
    name: string
    stage?: CmDealStage
    mrr: number
    closeDate?: string
    competitor?: string
    committed?: boolean
    isUpside?: boolean
    advisorInitiated?: boolean
    partnerIds?: string[]
}

export async function createCmDeal(token: string, data: CreateCmDealRequest): Promise<{ deal: CmDeal }> {
    const res = await cm.post('/api/cm/deals', data, authHeaders(token))
    return res.data
}

export interface UpdateCmDealRequest extends Partial<CreateCmDealRequest> {
    lostReason?: CmLostReason
    lostReasonDetail?: string
    pushReason?: string
}

export async function updateCmDeal(token: string, id: string, patch: UpdateCmDealRequest): Promise<{ deal: CmDeal }> {
    const res = await cm.patch(`/api/cm/deals/${id}`, patch, authHeaders(token))
    return res.data
}

export async function deleteCmDeal(token: string, id: string) {
    const res = await cm.delete(`/api/cm/deals/${id}`, authHeaders(token))
    return res.data
}

export async function addDealPartner(token: string, dealId: string, partnerId: string): Promise<{ deal: CmDeal }> {
    const res = await cm.post(`/api/cm/deals/${dealId}/partners`, { partnerId }, authHeaders(token))
    return res.data
}

export async function removeDealPartner(token: string, dealId: string, partnerId: string): Promise<{ deal: CmDeal }> {
    const res = await cm.delete(`/api/cm/deals/${dealId}/partners/${partnerId}`, authHeaders(token))
    return res.data
}

// ============================================
// Activities
// ============================================

export async function listCmActivities(
    token: string,
    filters: { partnerId?: string; dealId?: string; tsdContactId?: string; limit?: number } = {}
): Promise<{ activities: CmActivity[] }> {
    const res = await cm.get('/api/cm/activities', { ...authHeaders(token), params: filters })
    return res.data
}

export interface CreateCmActivityRequest {
    type: CmActivityType
    partnerId?: string
    dealId?: string
    tsdContactId?: string
    meetingType?: CmMeetingType
    meetingNature?: CmMeetingNature
    sentiment?: CmSentiment
    notes?: string
    inbound?: boolean
    occurredAt?: string
}

export async function createCmActivity(token: string, data: CreateCmActivityRequest): Promise<{ activity: CmActivity }> {
    const res = await cm.post('/api/cm/activities', data, authHeaders(token))
    return res.data
}

export async function updateCmActivity(token: string, id: string, patch: Partial<CmActivity>): Promise<{ activity: CmActivity }> {
    const res = await cm.patch(`/api/cm/activities/${id}`, patch, authHeaders(token))
    return res.data
}

export async function deleteCmActivity(token: string, id: string) {
    const res = await cm.delete(`/api/cm/activities/${id}`, authHeaders(token))
    return res.data
}

// ============================================
// TSDs
// ============================================

export async function listCmTsdCompanies(token: string): Promise<{ companies: CmTsdCompany[] }> {
    const res = await cm.get('/api/cm/tsd-companies', authHeaders(token))
    return res.data
}

export async function getCmTsdCompany(token: string, id: string): Promise<{ company: CmTsdCompany }> {
    const res = await cm.get(`/api/cm/tsd-companies/${id}`, authHeaders(token))
    return res.data
}

export async function createCmTsdCompany(token: string, data: { name: string; quarterlyIntroTarget?: number }) {
    const res = await cm.post('/api/cm/tsd-companies', data, authHeaders(token))
    return res.data
}

export async function updateCmTsdCompany(token: string, id: string, patch: Partial<CmTsdCompany>) {
    const res = await cm.patch(`/api/cm/tsd-companies/${id}`, patch, authHeaders(token))
    return res.data
}

export async function listCmTsdContacts(token: string, tsdCompanyId?: string): Promise<{ contacts: CmTsdContact[] }> {
    const params = tsdCompanyId ? { tsdCompanyId } : {}
    const res = await cm.get('/api/cm/tsd-contacts', { ...authHeaders(token), params })
    return res.data
}

export async function createCmTsdContact(token: string, data: any) {
    const res = await cm.post('/api/cm/tsd-contacts', data, authHeaders(token))
    return res.data
}

export async function updateCmTsdContact(token: string, id: string, patch: Partial<CmTsdContact>) {
    const res = await cm.patch(`/api/cm/tsd-contacts/${id}`, patch, authHeaders(token))
    return res.data
}

export async function getCmTsdContact(token: string, id: string): Promise<{ contact: CmTsdContact }> {
    const res = await cm.get(`/api/cm/tsd-contacts/${id}`, authHeaders(token))
    return res.data
}

export async function deleteCmTsdCompany(token: string, id: string) {
    const res = await cm.delete(`/api/cm/tsd-companies/${id}`, authHeaders(token))
    return res.data
}

export async function deleteCmTsdContact(token: string, id: string) {
    const res = await cm.delete(`/api/cm/tsd-contacts/${id}`, authHeaders(token))
    return res.data
}

// ============================================
// Playbooks
// ============================================

export interface PlaybookTemplate {
    key: string
    name: string
    priority: CmPriority
    description: string
    stepCount: number
    durationDays: number
}

export async function listPlaybookTemplates(token: string): Promise<{ templates: PlaybookTemplate[] }> {
    const res = await cm.get('/api/cm/playbook-templates', authHeaders(token))
    return res.data
}

export async function listCmPlaybooks(token: string): Promise<{ playbooks: CmPlaybook[] }> {
    const res = await cm.get('/api/cm/playbooks', authHeaders(token))
    return res.data
}

export async function getCmPlaybook(token: string, id: string): Promise<{ playbook: CmPlaybook }> {
    const res = await cm.get(`/api/cm/playbooks/${id}`, authHeaders(token))
    return res.data
}

export async function createPlaybookFromTemplate(
    token: string,
    data: { templateKey: string; partnerId?: string; name?: string; priority?: CmPriority }
) {
    const res = await cm.post('/api/cm/playbooks/from-template', data, authHeaders(token))
    return res.data
}

export async function createCustomPlaybook(token: string, data: any) {
    const res = await cm.post('/api/cm/playbooks/custom', data, authHeaders(token))
    return res.data
}

export interface AiPlaybookMessage {
    role: 'user' | 'assistant'
    content: string
}

export interface AiPlaybookDraft {
    name: string
    priority: CmPriority
    steps: Array<{ day: number; phase: string | null; title: string; description: string | null }>
}

export interface AiPlaybookChatResponse {
    reply: string
    askingFor: string | null
    draftPlaybook: AiPlaybookDraft | null
    error?: string
}

export async function aiPlaybookChat(
    token: string,
    data: { partnerId?: string; history: AiPlaybookMessage[] }
): Promise<AiPlaybookChatResponse> {
    const res = await cm.post('/api/cm/playbooks/ai/chat', data, authHeaders(token))
    return res.data
}

export async function updatePlaybookStep(token: string, id: string, status: CmStepStatus) {
    const res = await cm.patch(`/api/cm/playbook-steps/${id}`, { status }, authHeaders(token))
    return res.data
}

export async function updatePlaybookStepContent(token: string, id: string, data: { title?: string; description?: string }) {
    const res = await cm.patch(`/api/cm/playbook-steps/${id}`, data, authHeaders(token))
    return res.data
}

export async function updateCmPlaybook(token: string, id: string, data: { outcomeNotes?: string | null }) {
    const res = await cm.patch(`/api/cm/playbooks/${id}`, data, authHeaders(token))
    return res.data
}

export async function deleteCmPlaybook(token: string, id: string) {
    const res = await cm.delete(`/api/cm/playbooks/${id}`, authHeaders(token))
    return res.data
}

// ============================================
// Contact Groups
// ============================================

export async function listCmGroups(token: string): Promise<{ groups: CmContactGroup[] }> {
    const res = await cm.get('/api/cm/groups', authHeaders(token))
    return res.data
}

export async function getCmGroup(token: string, id: string): Promise<{ group: CmContactGroup }> {
    const res = await cm.get(`/api/cm/groups/${id}`, authHeaders(token))
    return res.data
}

export async function createCmGroup(token: string, data: { name: string; cadenceDays?: number; autoActions?: boolean; partnerIds?: string[] }) {
    const res = await cm.post('/api/cm/groups', data, authHeaders(token))
    return res.data
}

export async function createCmGroupFromCsv(token: string, data: { name: string; csv: string; cadenceDays?: number; autoActions?: boolean }) {
    const res = await cm.post('/api/cm/groups/csv', data, authHeaders(token))
    return res.data
}

export async function updateCmGroup(token: string, id: string, patch: Partial<CmContactGroup>) {
    const res = await cm.patch(`/api/cm/groups/${id}`, patch, authHeaders(token))
    return res.data
}

export async function addCmGroupMembers(token: string, id: string, partnerIds: string[]) {
    const res = await cm.post(`/api/cm/groups/${id}/members`, { partnerIds }, authHeaders(token))
    return res.data
}

export async function removeCmGroupMember(token: string, id: string, partnerId: string) {
    const res = await cm.delete(`/api/cm/groups/${id}/members/${partnerId}`, authHeaders(token))
    return res.data
}

export async function deleteCmGroup(token: string, id: string) {
    const res = await cm.delete(`/api/cm/groups/${id}`, authHeaders(token))
    return res.data
}

export async function runGroupPlaybook(token: string, groupId: string, templateKey: string): Promise<{ created: number; total: number }> {
    const res = await cm.post(`/api/cm/groups/${groupId}/run-playbook`, { templateKey }, authHeaders(token))
    return res.data
}

// ============================================
// Today's Actions + KPIs + Intelligence
// ============================================

export async function getTodaysActions(token: string): Promise<CmTodaysActions> {
    const res = await cm.get('/api/cm/todays-actions', authHeaders(token))
    return res.data
}

export async function completeAction(token: string, actionKey: string) {
    const res = await cm.post(`/api/cm/todays-actions/${actionKey}/complete`, {}, authHeaders(token))
    return res.data
}

export async function getCmNotificationCount(token: string) {
    const res = await cm.get('/api/cm/notifications/count', authHeaders(token))
    return res.data
}

export async function getCmKpis(token: string): Promise<CmKpis> {
    const res = await cm.get('/api/cm/kpis', authHeaders(token))
    return res.data
}

export async function getCmDiagnostics(token: string): Promise<{ diagnostics: CmDiagnosticRow[] }> {
    const res = await cm.get('/api/cm/intelligence/diagnostics', authHeaders(token))
    return res.data
}

export interface CmSignalsResponse {
    signals: CmSignal[]
    totals: { growth: number; churn: number; stall: number; intel?: number }
    capped: { growth: boolean; churn: boolean; stall: boolean; intel?: boolean }
}

export async function getCmSignals(token: string): Promise<CmSignalsResponse> {
    const res = await cm.get('/api/cm/intelligence/signals', authHeaders(token))
    return res.data
}
